// @flow

import { type UserInputValidator, type UserInputValidationError, type UserInputValidatorArgs } from './types';

import { chileanValidator } from '@tectonics/prs/chile/validation';
import { mexicanValidator } from '@tectonics/prs/mexico/validation';
import { peruvianValidator } from '@tectonics/prs/peru/validation';
import { brazilianValidator } from '@tectonics/prs/brazil/validation';
import { colombianValidator } from '@tectonics/prs/colombia/validation';
import { argentineValidator } from '@tectonics/prs/argentina/validation';
import { uruguayanValidator } from '@tectonics/prs/uruguay/validation';
import { ecuadorianValidator } from '@tectonics/prs/ecuador/validation';
import { bolivianValidator } from '@tectonics/prs/bolivia/validation';
import { costaRicanValidator } from '@tectonics/prs/costa-rica/validation';
import { paraguayanValidator } from '@tectonics/prs/paraguay/validation';
import { panamanianValidator } from '@tectonics/prs/panama/validation';
import { elSalvadorValidator } from '@tectonics/prs/el-salvador/validation';
import { southAfricaValidator } from '@tectonics/prs/south-africa/validation';
import { kenyaValidator } from '@tectonics/prs/kenya/validation';
import { nigeriaValidator } from '@tectonics/prs/nigeria/validation';
import { indiaValidator } from '@tectonics/prs/india/validation';
import { philippinesValidator } from '@tectonics/prs/philippines/validation';
import { ugandaValidator } from '@tectonics/prs/uganda/validation';
import { ghanaValidator } from '@tectonics/prs/ghana/validation';
import { thailandValidator } from '@tectonics/prs/thailand/validation';
import { turkeyValidator } from '@tectonics/prs/turkey/validation';
import { indonesiaValidator } from '@tectonics/prs/indonesia/validation';
import { malaysiaValidator } from '@tectonics/prs/malaysia/validation';

import { voidValidator } from '@tectonics/prs/validation';
import { customerValidator } from '@tectonics/prs/core/customer/validation';

import { tefValidator } from '@tectonics/prs/tef/validation';
import { cardValidator } from '@tectonics/prs/card/validation';
import { safetypayValidator } from '@tectonics/prs/safetypay/validation';
import { eftValidator } from '@tectonics/prs/eft/validation';
import { argentineCashValidator } from '@tectonics/prs/cash-argentina/validation';
import { ewalletValidator } from '@tectonics/prs/ewallet/validation';
import { voucherCardValidator } from '@tectonics/prs/vouchercard/validation';

import { countrySchemaRegistry, type Country } from '@tectonics/prs/country';
import { buildValidator } from '@tectonics/prs/validation/integrated-validator';
import { cryptocurrencyValidator } from '@tectonics/prs/cryptocurrency/validation';
import { genericValidator } from '@tectonics/prs/generic-fields/validation';

export function getUserInputValidator(selectedPaymentMethodCode: string, country: Country): UserInputValidator {
  return compositeValidator([
    customerValidator,
    genericValidator,
    getCountryValidator(country),
    getPaymentMethodValidator(selectedPaymentMethodCode, country),
  ]);
}

function getCountryValidator(country: Country): UserInputValidator {
  const countrySchema = countrySchemaRegistry.get(country);
  if (countrySchema) return buildValidator(countrySchema);

  switch (country) {
    case 'br':
      return brazilianValidator;
    case 'ar':
      return argentineValidator;
    case 'bo':
      return bolivianValidator;
    case 'cl':
      return chileanValidator;
    case 'co':
      return colombianValidator;
    case 'ec':
      return ecuadorianValidator;
    case 'mx':
      return mexicanValidator;
    case 'pe':
      return peruvianValidator;
    case 'uy':
      return uruguayanValidator;
    case 'cr':
      return costaRicanValidator;
    case 'py':
      return paraguayanValidator;
    case 'pa':
      return panamanianValidator;
    case 'sv':
      return elSalvadorValidator;
    case 'gt':
    case 'do':
    case 'eg':
    case 'jm':
    case 'tz':
      return voidValidator;
    case 'za':
      return southAfricaValidator;
    case 'ke':
      return kenyaValidator;
    case 'ng':
      return nigeriaValidator;
    case 'in':
      return indiaValidator;
    case 'ph':
      return philippinesValidator;
    case 'ug':
      return ugandaValidator;
    case 'gh':
      return ghanaValidator;
    case 'th':
      return thailandValidator;
    case 'tr':
      return turkeyValidator;
    case 'id':
      return indonesiaValidator;
    case 'my':
      return malaysiaValidator;

    default:
      throw new Error(`No customer form validator for country ${country}`);
  }
}

function compositeValidator(validators: ImmutableArray<UserInputValidator>): UserInputValidator {
  return (args: UserInputValidatorArgs): ?UserInputValidationError => {
    for (const validator of validators) {
      const result = validator(args);

      if (result) {
        return result;
      }
    }

    return undefined;
  };
}

function getPaymentMethodValidator(selectedPaymentMethod: string, country: Country): UserInputValidator {
  switch (selectedPaymentMethod) {
    case 'creditcard':
    case 'debitcard':
    case 'multibeneficios':
      return cardValidator;

    case 'tef':
      return tefValidator;

    case 'boleto':
    case 'banktransfer':
    case 'oxxo':
    case 'oxxopay':
    case 'google':
    case 'pix':
    case 'pix-pisp':
    case 'externalpayprovider':
    case 'nupay':
    case 'itau-pay':
    case 'mpcredito':
    case 'meeza':
    case 'nibbs':
    case 'transfiya':
    case 'breb':
    case 'qr':
    case 'sencillito':
    case 'spei':
    case 'webpay':
    case 'pagoefectivo':
    case 'multicaja':
    case 'servipag':
    case 'safetypay-cash':
    case 'safetypay-online':
      return voidValidator;

    case 'cryptocurrency':
      return cryptocurrencyValidator;

    case 'paynet':
    case 'cash':
      return cashValidatorResolver(country);

    case 'eft':
      return eftValidator;

    case 'safetypay':
      return safetypayValidator;

    case 'ewallet':
      return ewalletValidator;

    case 'vouchercard':
      return voucherCardValidator;

    case '':
      return voidValidator;

    default:
      throw new Error(`validator for method: ${selectedPaymentMethod} not found`);
  }
}

function cashValidatorResolver(country: Country): UserInputValidator {
  switch (country) {
    case 'mx':
    case 'bo':
    case 'co':
    case 'gt':
    case 'cr':
    case 'pa':
    case 'sv':
      return voidValidator;
    case 'ar':
      return argentineCashValidator;
    default:
      throw new Error(`validator for method cash in country ${country} not found`);
  }
}
